import React, { Component } from 'react';
import {
  Eye,
  EyeSlash,
  WarningCircle,
  WhatsappLogo,
  Copy,
  ArrowLeft,
} from '@phosphor-icons/react';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { observable, makeObservable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import axiosAuthAPI from '../api/axiosAuthAPI';

@inject('store')
@observer
class Login extends Component {
  @observable email = '';
  @observable password = '';
  @observable errorMessage = '';
  @observable isPasswordVisible = false; // State for password visibility
  @observable accountDisabled = false;
  @observable disabledReason = ''; // 'manual' or 'inactivity'
  @observable showForgotPassword = false;
  @observable forgotPasswordStatus = 'initial'; // initial, loading, success, error
  backgroundImages = [
    '/login_1.jpg',
    '/login_2.jpg',
    '/login_3.jpg',
    '/login_4.jpg',
    '/login_1.jpg',
    '/login_3.jpg',
    '/login_5.jpg',
    '/login_6.jpg',
    '/login_7.jpg',
    '/login_8.jpg',
  ];

  constructor() {
    super();
    makeObservable(this);
    this.randomBackgroundImage =
      this.backgroundImages[
        Math.floor(Math.random() * this.backgroundImages.length)
      ];
  }

  onChangeAny = (val, attr) => {
    this[attr] = val;
    this.errorMessage = '';
  };

  togglePasswordVisibility = () => {
    this.isPasswordVisible = !this.isPasswordVisible; // Toggle the visibility
  };

  onLogin = async e => {
    e.preventDefault();

    try {
      const result = await this.props.store.login(this.email, this.password);

      if (result === false) {
        this.errorMessage = 'E-mail o contraseña incorrectos';
      } else if (result && result.disabled) {
        this.accountDisabled = true;
        this.disabledReason = result.reason;
      } else if (result && result.errorMessage) {
        this.errorMessage = result.errorMessage;
      } else if (result && result.subscriptionSetupRequired) {
        // Backend indicates user needs to complete subscription setup via requires_action
        // This is the most reliable indicator for newly registered users
        console.log('User needs to complete subscription setup:', result);

        // Display a notification to the user
        this.errorMessage =
          result.message ||
          'Es necesario completar la configuración de la suscripción para acceder a la plataforma.';

        // Skip the additional subscription status check and go directly to setup
        setTimeout(() => {
          this.props.history.push('/student/subscriptions/setup');
        }, 1000);
      } else if (result && result.subscriptionType) {
        // Handle different subscription types from the subscription status endpoint
        // This is used for users who already have some subscription record
        switch (result.subscriptionType) {
          case 'free_user':
          case 'paid':
            // Free users and paid users get full access - redirect to dashboard
            this.props.history.push('/');
            break;
          case 'needs_setup':
            // Users needing setup redirect to subscription setup
            // This is a second path to detect incomplete setups
            console.log(
              'User has pending subscription setup (needs_setup type)'
            );
            this.props.history.push('/student/subscriptions/setup');
            break;
          case 'canceled':
            // Users with canceled subscriptions redirect to subscription status
            this.props.history.push('/student/subscriptions/status');
            break;
          default:
            // Default behavior for unrecognized types
            this.props.history.push('/');
        }
      } else if (result === true) {
        // Check if we have a pending subscription from registration
        const pendingSubscription = sessionStorage.getItem(
          'pendingSubscription'
        );
        if (pendingSubscription) {
          // User has just registered and needs to set up subscription
          console.log('Pending subscription found in session storage');
          this.props.history.push('/student/subscriptions/setup');
        }
        // If no pending subscription, the store.login will trigger the default redirect
      }
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage =
        'Error al iniciar sesión. Por favor, inténtelo de nuevo.';
    }
  };

  // Helper function to generate message template for admins
  getAdminMessageTemplate = () => {
    return `Hola, soy el estudiante ${this.email}. Mi cuenta ha sido desactivada ${this.disabledReason === 'inactivity' ? 'por inactividad' : 'por el administrador'} y necesito que la reactiven para poder continuar con mi formación. Gracias.`;
  };

  // Copy message to clipboard
  copyMessageToClipboard = () => {
    navigator.clipboard.writeText(this.getAdminMessageTemplate());
  };

  @action
  toggleForgotPassword = () => {
    this.showForgotPassword = !this.showForgotPassword;
    this.forgotPasswordStatus = 'initial';
    this.errorMessage = '';
  };

  @action
  requestPasswordReset = async e => {
    e.preventDefault();
    if (!this.email) {
      this.errorMessage = 'Por favor, introduce tu email';
      return;
    }

    this.forgotPasswordStatus = 'loading';
    this.errorMessage = '';

    try {
      await axiosAuthAPI.post('/api/v1/auth/password-reset-request', {
        email: this.email,
        realm: 'auto',
      });

      // Always show success message even if email doesn't exist
      // This prevents account enumeration
      this.forgotPasswordStatus = 'success';
    } catch (error) {
      console.error('Error requesting password reset:', error);
      this.forgotPasswordStatus = 'error';
      this.errorMessage =
        'Error al solicitar el restablecimiento de contraseña. Por favor, inténtelo de nuevo.';
    }
  };

  render() {
    return (
      <>
        <Helmet>
          <title>{`Login`}</title>
        </Helmet>
        <div className="flex flex-col md:flex-row h-screen">
          <div className="w-full md:w-2/3 p-8 overflow-y-auto flex items-center justify-center">
            <div className="w-full md:max-w-md 2xl:max-w-xl">
              <div className="bg-white rounded-xl shadow-xl transform transition-all shadow-md hover:shadow-2xl focus:shadow-2xl">
                <div className="px-4 py-4 md:px-12 md:py-12">
                  <Switch>
                    <Route path="/login">
                      {this.accountDisabled ? (
                        <DisabledAccount
                          disabledReason={this.disabledReason}
                          email={this.email}
                          messageTemplate={this.getAdminMessageTemplate()}
                          copyMessageToClipboard={this.copyMessageToClipboard}
                        />
                      ) : this.showForgotPassword ? (
                        <ForgotPassword
                          email={this.email}
                          onChange={val => this.onChangeAny(val, 'email')}
                          onSubmit={this.requestPasswordReset}
                          onCancel={this.toggleForgotPassword}
                          status={this.forgotPasswordStatus}
                          errorMessage={this.errorMessage}
                        />
                      ) : (
                        <Logon
                          email={this.email}
                          password={this.password}
                          onChange={this.onChangeAny}
                          onLogin={this.onLogin}
                          onForgotPassword={this.toggleForgotPassword}
                          errorMessage={this.errorMessage}
                          isPasswordVisible={this.isPasswordVisible} // Pass the password visibility state
                          togglePasswordVisibility={
                            this.togglePasswordVisibility
                          } // Pass the function to toggle
                        />
                      )}
                    </Route>
                    <Route>
                      <Redirect to="/login" />
                    </Route>
                  </Switch>
                </div>
              </div>
            </div>
          </div>
          <div
            className="hidden md:block w-1/2 bg-cover bg-center"
            style={{ backgroundImage: `url('${this.randomBackgroundImage}')` }}
          ></div>
        </div>
      </>
    );
  }
}

const Logon = observer(
  ({
    email,
    password,
    onChange,
    onLogin,
    onForgotPassword,
    errorMessage,
    isPasswordVisible,
    togglePasswordVisibility,
  }) => {
    return (
      <>
        <form onSubmit={onLogin}>
          <div
            className={`mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16`}
          >
            <a
              href="https://www.vrtus.app/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/logo_dark.jpg"
                alt="Logo"
                className="h-16 w-16 rounded-full object-cover cursor-pointer"
              />
            </a>
          </div>
          <div className="mt-3 text-center ">
            <div className="text-3xl font-medium text-gray-900">
              Iniciar sesión
            </div>
            <div className="flex flex-col flex-1">
              <label className="text-gray-400 text-sm block mt-4 inline-block text-left">
                E-mail
              </label>
              <input
                value={email}
                onChange={e => onChange(e.target.value, 'email')}
                type="email"
                className="rounded-md text-lg px-4 py-2 border border-gray-300 "
                placeholder="sonia@autoescuela.es"
              />
            </div>
            <div className="flex flex-col flex-1 relative">
              {' '}
              {/* Make the parent relative for positioning */}
              <label className="text-gray-400 text-sm block mt-4 inline-block text-left">
                Contraseña
              </label>
              <input
                value={password}
                onChange={e => onChange(e.target.value, 'password')}
                type={isPasswordVisible ? 'text' : 'password'} // Toggle input type based on visibility
                className="rounded-md text-lg px-4 py-2 border border-gray-300 inline-block pr-10" // Add padding to the right for the icon
                placeholder="*******"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-12 text-gray-500 focus:outline-none"
              >
                {isPasswordVisible ? <Eye size={20} /> : <EyeSlash size={20} />}{' '}
                {/* Toggle icon */}
              </button>
            </div>
            <div className="flex flex-col">
              <button
                type="submit"
                className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white mt-4 border border-gray-300 inline-block w-full"
              >
                Entrar
              </button>

              <div className="mt-4 text-center">
                <span className="text-gray-500 text-sm">
                  ¿No tienes cuenta?{' '}
                </span>
                <a
                  href="/register"
                  className="text-gray-700 text-sm font-medium hover:underline"
                >
                  Regístrate aquí
                </a>
              </div>

              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-gray-600 text-sm font-medium hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </div>
          </div>
        </form>
        {errorMessage && (
          <div className="text-red-600 bg-red-50 rounded-md p-1 text-center mt-4">
            {errorMessage}
          </div>
        )}
      </>
    );
  }
);

const DisabledAccount = observer(
  ({ disabledReason, email, messageTemplate, copyMessageToClipboard }) => {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = () => {
      copyMessageToClipboard();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    return (
      <>
        <div className="text-center">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full bg-red-300">
            <WarningCircle
              className="h-10 w-10 text-red-700"
              aria-hidden="true"
            />
          </div>

          <div className="mt-3">
            <div className="text-2xl font-medium text-gray-900 mb-4">
              {disabledReason === 'inactivity'
                ? 'Cuenta desactivada por inactividad'
                : 'Cuenta desactivada'}
            </div>

            <div className="bg-red-50 p-4 rounded-lg text-left mb-5">
              <p className="text-red-700 mb-2">
                {disabledReason === 'inactivity'
                  ? 'Su cuenta ha sido desactivada debido a inactividad. Por favor, contacte con su autoescuela para reactivar su cuenta.'
                  : 'Su cuenta ha sido desactivada por el administrador. Por favor, contacte con su autoescuela para obtener ayuda.'}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg text-left mb-5">
              <div className="font-medium text-gray-700 mb-2">
                Contacte a su administrador:
              </div>
              <div className="flex justify-between items-center bg-white p-3 rounded border border-gray-300 mb-3">
                <div className="text-sm text-gray-500 overflow-hidden mr-2">
                  {messageTemplate}
                </div>
                <button
                  onClick={handleCopy}
                  className={`flex items-center justify-center ${copied ? 'bg-blue-500' : 'bg-green-500'} text-white rounded-full h-8 w-8 hover:bg-green-600`}
                  title="Copiar mensaje"
                >
                  {copied ? (
                    <span className="text-xs font-bold">✓</span>
                  ) : (
                    <Copy size={18} />
                  )}
                </button>
              </div>

              <a
                href={`https://wa.me/?text=${encodeURIComponent(messageTemplate)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 w-full"
              >
                <WhatsappLogo size={24} className="mr-2" />
                Contactar por WhatsApp
              </a>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white mt-4 border border-gray-300 inline-block"
            >
              Volver a intentar
            </button>
          </div>
        </div>
      </>
    );
  }
);

const ForgotPassword = observer(
  ({ email, onChange, onSubmit, onCancel, status, errorMessage }) => {
    return (
      <>
        <div className="text-center">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16">
            <a
              href="https://www.vrtus.app/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/logo_dark.jpg"
                alt="Logo"
                className="h-16 w-16 rounded-full object-cover cursor-pointer"
              />
            </a>
          </div>

          <div className="mt-3">
            <div className="text-2xl font-medium text-gray-900 mb-4">
              Restablecer contraseña
            </div>

            {status === 'success' ? (
              <div className="text-left">
                <div className="bg-green-50 p-4 rounded-lg mb-5">
                  <p className="text-green-700 mb-2">
                    Si existe una cuenta con ese correo electrónico, recibirás
                    instrucciones para restablecer tu contraseña en breve.
                  </p>
                  <p className="text-green-700 mb-2">
                    Por favor, revisa tu bandeja de entrada y también la carpeta
                    de spam.
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="flex items-center justify-center rounded-lg text-lg px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white w-full"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Volver al inicio de sesión
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <div className="text-left mb-5">
                  <p className="text-gray-600 mb-4">
                    Introduce tu dirección de correo electrónico y te enviaremos
                    un enlace para restablecer tu contraseña.
                  </p>

                  <div className="flex flex-col">
                    <label className="text-gray-400 text-sm block mb-1">
                      E-mail
                    </label>
                    <input
                      value={email}
                      onChange={e => onChange(e.target.value)}
                      type="email"
                      className="rounded-md text-lg px-4 py-2 border border-gray-300"
                      placeholder="sonia@autoescuela.es"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col mt-6">
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className={`font-medium rounded-lg text-lg px-4 py-2 ${
                      status === 'loading'
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gray-500 hover:bg-gray-600'
                    } text-white border border-gray-300 w-full mb-3`}
                  >
                    {status === 'loading'
                      ? 'Enviando...'
                      : 'Enviar enlace de restablecimiento'}
                  </button>

                  <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center justify-center text-gray-600 hover:text-gray-800"
                  >
                    <ArrowLeft size={18} className="mr-1" />
                    Volver al inicio de sesión
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {errorMessage && status !== 'success' && (
          <div className="text-red-600 bg-red-50 rounded-md p-2 text-center mt-4">
            {errorMessage}
          </div>
        )}
      </>
    );
  }
);

export default withRouter(Login);
